import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
    Typography,
} from "@mui/material";

import { IconPlus, IconEdit, IconEye, IconTrash, IconSquareArrowRight } from '@tabler/icons-react';

import { TableContainer } from '../../Styles/CustomViews/Pages/ListComponent/customListComponentStyles';

import { useBillingHook } from '../../Hooks/Billings/billingHooks';

import { Billing, ListBilling } from '../../Models/Billing/Billing';
import { LegendItemInterface } from '../../Models/Common/List/LegendInterface';
import { Authentication } from "../../Models/Authentication/Authentication";

import QueryBuilderHelper from '../../Utilities/Helpers/QueryBuilderHelper';
import RouteHelper from '../../Utilities/Helpers/RouteHelper';
import DateHelper from '../../Utilities/Helpers/DateHelper';
import StatusHelper from '../../Utilities/Helpers/StatusHelper';
import GlobalFunctionHelper from '../../Utilities/Helpers/GlobalFunctionHelper';
import AccessibilityHelper from '../../Utilities/Helpers/AccessibilityHelper';
import LocalStorageHelper from '../../Utilities/Helpers/LocalStorageHelper';

import { RouteEnum } from '../../Utilities/Enums/RouteEnums';
import { CachingEnum } from '../../Utilities/Enums/CachingEnum';
import { ButtonType } from '../../Utilities/Enums/ButtonTypeEnum';
import { ViewType } from '../../Utilities/Enums/ViewTypeEnum';
import { BillingStatus, BillingStatusEnum } from '../../Utilities/Enums/StatusEnums';
import { ListBillingField, AuthenticationField } from '../../Utilities/Enums/ParametersEnum';
import { AdminModules } from '../../Utilities/Enums/AdminModulesEnum';
import { OrderByEnum } from '../../Utilities/Enums/SortEnums';
import { RoleTypeEnum } from '../../Utilities/Enums/RolesEnums';

import CustomListComponent from '../../CustomViews/Pages/ListComponent/CustomListComponent';
import CustomIconButton from '../../CustomViews/Elements/CustomIconButton';
import CustomDeleteConfirmation from '../../CustomViews/Elements/CustomDeleteConfirmation/CustomDeleteConfirmation';
import CustomButton from '../../CustomViews/Elements/CustomButton';
import LicenseKeyTitle from '../../CustomViews/Pages/CustomTitles/LicenseKeyTitle';
import CustomListHeader from '../../CustomViews/Pages/ListComponent/CustomListHeader';
import CustomListSearch from '../../CustomViews/Pages/ListComponent/Search/CustomListSearch';
import FormDateRangePicker from '../../CustomViews/Elements/FormDateRangePicker';
import { CustomListSearchResetBtns } from '../../CustomViews/Pages/ListComponent/Search/CustomListSearchResetBtns';
import NewTab from '../../CustomViews/Pages/NewTab/NewTab';
import VendorsDropdown from '../../CustomViews/Elements/AutoComplete/VendorsDropdown';

export default function BillingList() {
    const location = useLocation();
    const navigate = useNavigate();
    let _userData: Authentication = LocalStorageHelper.getUserData();

    //search params
    const queryParams = new URLSearchParams(location.search);
    const [searchParams, setSearchParams] = useSearchParams(queryParams);

    //states
    const [queryStr, setQueryStr] = useState({
        pageSize: QueryBuilderHelper.DefaultPageSizeValue(),
        pageNumber: QueryBuilderHelper.DefaultPageNumValue(),
        searchString: QueryBuilderHelper.DefaultSearchValue(),
        status: QueryBuilderHelper.DefaultQueryFilterValue('status') ?? 0,
        invoiceDateFrom: QueryBuilderHelper.DefaultQueryFilterValue('invoiceDateFrom', null),
        invoiceDateTo: QueryBuilderHelper.DefaultQueryFilterValue('invoiceDateTo', null),
        sortBy: QueryBuilderHelper.DefaultSortValue(ListBillingField.TRANS_NO),
        orderBy: QueryBuilderHelper.DefaultOrderValue(OrderByEnum.DESC),
        vendorPublicId: QueryBuilderHelper.DefaultQueryFilterValue('vendorPublicId', null),
    });
    const [openDelete, setopenDelete] = useState(false);
    let [toDeleteItem, settoDeleteItem] = useState<any>(null);

    const q_queryKey = [CachingEnum.BILLINGS, queryStr]
    const { data, fetchStatus, deleteBillingById } = useBillingHook(q_queryKey, queryStr);

    const tableColumns = [
        {
            accessor: ListBillingField.TRANS_NO,
            title: 'Invoice Number',
            sortable: true,
            render: (record: ListBilling) => (record?.[ListBillingField.PUBLIC_ID] ? 
                <NewTab 
                linkTitle={record?.[ListBillingField.TRANS_NO] != null && record?.[ListBillingField.TRANS_NO] != "" ? record?.[ListBillingField.TRANS_NO] : "--"}
                linkUrl={`${RouteEnum.BILLING_DETAILS}?id=${record?.[ListBillingField.PUBLIC_ID] }&view=${ViewType.PREVIEW}`} /> : "--"
            ),
        },
        {
            accessor: ListBillingField.INVOICE_DATE,
            title: 'Invoice Date',
            sortable: true,
            render: (record: ListBilling) => record?.[ListBillingField.INVOICE_DATE] ? DateHelper.getFormattedDate(record?.[ListBillingField.INVOICE_DATE], "dd/MM/yyyy") : "--",
        },
        {
            accessor: ListBillingField.COMPANY_NAME,
            title: 'Billed Company',
            sortable: true,
            render: (record: ListBilling) => record?.[ListBillingField.COMPANY_NAME] ?? "--",
        },
        {
            accessor: ListBillingField.TOTAL_AFTER_ADJUSTMENT,
            title: 'Amount',
            sortable: true,
            render: (record: ListBilling) =>  record?.[ListBillingField.CURRENCY_CODE] + GlobalFunctionHelper.amountFormatter(record?.[ListBillingField.TOTAL_AFTER_ADJUSTMENT] ?? 0, 2),
        },
        {
            accessor: ListBillingField.GENERATED_LICENSE_KEY,
            title: 'Generated License',
            sortable: true,
            render: (record: ListBilling) => record?.[ListBillingField.GENERATED_LICENSE_KEY] ? <LicenseKeyTitle licenseKey={record?.[ListBillingField.GENERATED_LICENSE_KEY] ?? ""} color="inherit" /> : "--",
        },
        {
            accessor: ListBillingField.STATUS,
            sortable: true,
            render: (record: ListBilling) => StatusHelper.getBillingStatus(record?.[ListBillingField.STATUS] != BillingStatusEnum.ALL ? (record?.[ListBillingField.STATUS] ?? BillingStatusEnum.UNPAID) : BillingStatusEnum.UNPAID),
        },
        {
            accessor: 'action',
            title: "Action",
            render: (record: ListBilling) => <>
                {AccessibilityHelper.hasAccess(AdminModules.BILLING_EDIT) && record?.[ListBillingField.STATUS] != BillingStatusEnum.PAID && <CustomIconButton toolTipName='Checkout'
                    icon={<IconSquareArrowRight />}
                    onClick={() => { navigate(`${RouteEnum.BILLING_DETAILS}?id=${record?.[ListBillingField.PUBLIC_ID] }&view=${ViewType.PREVIEW}&isCheckout=true`, { replace: true }) }} />}
                <CustomIconButton toolTipName='View'
                    icon={<IconEye />}
                    onClick={() => { navigate(`${RouteEnum.BILLING_DETAILS}?id=${record?.[ListBillingField.PUBLIC_ID] }&view=${ViewType.PREVIEW}`, { replace: true }) }} />
                {AccessibilityHelper.hasAccess(AdminModules.BILLING_EDIT) && record?.[ListBillingField.STATUS] != BillingStatusEnum.PAID && <CustomIconButton toolTipName='Edit'
                    icon={<IconEdit />}
                    onClick={() => { navigate(`${RouteEnum.BILLING_DETAILS}?id=${record?.[ListBillingField.PUBLIC_ID] }&view=${ViewType.EDIT}`, { replace: true }) }} />}
                {AccessibilityHelper.hasAccess(AdminModules.BILLING_DELETE) && record?.[ListBillingField.STATUS] != BillingStatusEnum.PAID && <CustomIconButton toolTipName='Delete'
                    icon={<IconTrash color='var(--buttonHoverBGDangerColor)' />}
                    onClick={() => {
                        setopenDelete(true);
                        settoDeleteItem(record);
                    }} />}
            </>
        },
    ];

    useEffect(() => {
        setSearchParams(QueryBuilderHelper.CleanQueryDictionary(queryStr));
    }, [queryStr]);

    return <TableContainer $hadBoxShadow={true}>
        <CustomListHeader title={RouteHelper.getRouteDescription(RouteEnum.BILLINGS)} />
        <CustomListSearch searchValue={queryStr.searchString ?? ""}
            onChangeSearch={(searchString) => setQueryStr(prev => ({ ...prev, searchString: searchString, pageNumber: 1 }))}
            searchPlaceholder='Search Invoice / Company'
            legends={data?.legends}
            onClickLegendItem={(id) => setQueryStr(prev => ({ ...prev, status: id, pageNumber: 1 }))}>

            <FormDateRangePicker name="invoiceDate"
                hideLabel
                title=''
                placeholder='Invoice Date'
                fieldValue={[queryStr.invoiceDateFrom, queryStr.invoiceDateTo] ?? []}
                handleOnChange={(selectedValue: any) => {
                setQueryStr(prev => ({
                    ...prev,
                    // invoiceDateFrom: selectedValue[0] ? new Date(selectedValue[0]) : new Date(),
                    // invoiceDateTo: selectedValue[1] ? new Date(selectedValue[1]) : new Date(),
                    // invoiceDateFrom: selectedValue[0] ?? DateHelper.getFormattedDate(new Date(), "yyyy-MM-dd"),
                    // invoiceDateTo: selectedValue[1] ?? DateHelper.getFormattedDate(new Date(), "yyyy-MM-dd"),
                    invoiceDateFrom: selectedValue[0] ?? null,
                    invoiceDateTo: selectedValue[1] ?? null,
                    pageNumber: 1,
                }));
            }} />

            {(_userData?.[AuthenticationField.ROLE_TYPE] == RoleTypeEnum.VENDOR_ADMIN || _userData?.[AuthenticationField.ROLE_TYPE] == RoleTypeEnum.SUPER_USER) && <VendorsDropdown name="vendorPublicId"
                hideLabel
                title=''
                placeholder='Vendor'
                fieldValue={queryStr.vendorPublicId}
                isUserVendorsOnly={_userData?.[AuthenticationField.ROLE_TYPE] == RoleTypeEnum.VENDOR_ADMIN}
                handleOnChange={(selectedValue: any) => setQueryStr(prev => ({
                    ...prev,
                    vendorPublicId: selectedValue?.id ?? null,
                    pageNumber: 1,
                }))} />}

            <CustomListSearchResetBtns onClickSearch={() => { }}
                onClickReset={() => {
                    setQueryStr(prev => ({
                        ...prev,
                        searchString: '',
                        invoiceDateFrom: null,
                        invoiceDateTo: null,
                        pageNumber: 1,
                        vendorPublicId: null,
                     }))
                }}/>
        </CustomListSearch>
        <CustomListComponent
            fetching={fetchStatus == "fetching"}
            columns={tableColumns}
            idAccessor={(record: ListBilling) => `billing${record?.[ListBillingField.PUBLIC_ID]}`}
            records={data?.data?.list ?? []}
            totalRecords={data?.data?.metaData?.totalCount ?? 0}
            page={queryStr.pageNumber}
            recordsPerPage={queryStr.pageSize}
            defaultColumnSort={{ columnAccessor: queryStr?.sortBy ?? 'id', direction: queryStr?.orderBy ?? 'asc' }}
            onSortStatusChange={((sort: any) => { setQueryStr((prev: any) => ({ ...prev, sortBy: sort?.columnAccessor ?? "id", orderBy: sort?.direction ?? "asc", pageNumber: 1 })) })}
            setPage={(p: number) => setQueryStr((prev: any) => ({ ...prev, pageNumber: p }))}
            onRecordsPerPageChange={(p: number) => setQueryStr((prev: any) => ({ ...prev, pageSize: p, pageNumber: 1 }))}
        />

        {openDelete && <CustomDeleteConfirmation
            openDialog={openDelete}
            handleClose={() => {
                setopenDelete(false);
                settoDeleteItem(null);
            }}
            showRemarksField={true}
            deleteItem={async (remarks: any) => {
                if(toDeleteItem?.[ListBillingField.PUBLIC_ID]) {
                    let deleteReponse: any = await deleteBillingById(toDeleteItem?.[ListBillingField.PUBLIC_ID], remarks);

                    if(deleteReponse?.isSuccess) {
                        setopenDelete(false);
                        settoDeleteItem(null);
                    }
                }
            }} />
        }
    </TableContainer>
}
